import { useEffect, useState } from 'react';
import { useElementBox, useElementRender, type ElementComponent } from './elementRenderContext';
import { boolProp } from './elementProps';
import { QuantityStepper } from './PurchaseControls';
import { buyButton, pricedLabel, usePurchase, VariantSelect } from './usePurchase';
import { money } from './storeFacts';
import './elementStickyBuy.css';
import { maxQuantity } from '../../commerce/variantStock';
import { responsive, THUMB_SIZES } from './responsiveImage';

// Sleek's sticky add-to-cart bar: once the page's own buy box has scrolled up out of sight, a bar rises
// from the bottom edge with the product, its option, the quantity and the button, so a long page never
// leaves the shopper scrolling back up to buy. It draws the same choice as the buy box, so they agree.
// A phone keeps the option and the button. While hidden it is inert, so the keyboard never lands in it.
// Whether to rise is read from the buy box's position, not an intersection observer: a jump from below
// the box to above it — a restored scroll, a fast fling — never crosses the screen, so an observer misses it.

// Says whether the shopper has scrolled past the page's buy box, read from its position at most once a frame.
function usePastBuyBox(productId: string | undefined): boolean {
  const [past, setPast] = useState(false);
  useEffect(() => {
    if (!productId) return;
    let frame = 0;
    const check = () => {
      frame = 0;
      const row = document.querySelector('.xe-purchase-row');
      setPast(row ? row.getBoundingClientRect().bottom < 0 : false);
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(check); };
    window.addEventListener('scroll', onScroll, { passive: true });
    check();
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(frame); };
  }, [productId]);
  return past;
}

// Draws the bar for the product in scope, risen once its buy box is out of sight, and in place in the builder.
export const StickyBuyElement: ElementComponent = ({ node }) => {
  const box = useElementBox(node);
  const buy = usePurchase();
  const { editing, store } = useElementRender();
  const past = usePastBuyBox(buy.product?.id);
  const { product, variant } = buy;
  if (!product) return null;
  const shown = past || editing;
  const price = Number(variant?.price ?? 0);
  const add = () => { if (variant && !buy.locked) buy.commerce?.add(product, variant.id, buy.quantity); };
  return (
    <div
      {...box} className={[box.className, 'xe-sticky-buy'].filter(Boolean).join(' ')} role="region" aria-label={product.title}
      data-shown={shown || undefined} data-editing={editing || undefined}
      {...(shown ? {} : ({ inert: '', 'aria-hidden': 'true' } as Record<string, string>))}
    >
      <div className="xe-sticky-buy-inner">
        {product.images[0] ? <img className="xe-sticky-buy-thumb" {...responsive(product.images[0].src, THUMB_SIZES)} alt="" loading="lazy" /> : null}
        <div className="xe-sticky-buy-words">
          <p dir="auto">{product.title}</p>
          <p className="xe-sticky-buy-price">{store ? money(price, store) : price}</p>
        </div>
        {product.variants.length > 1 ? <VariantSelect buy={buy} disabled={buy.locked} /> : null}
        {boolProp(node.props, 'showQuantity', true) ? <div className="xe-sticky-buy-qty"><QuantityStepper value={buy.quantity} max={maxQuantity(variant)} disabled={!buy.available && !buy.locked} onChange={buy.setQuantity} /></div> : null}
        <button type="button" className="xe-sticky-buy-add" {...buyButton(buy.locked, buy.available)} onClick={add}>{pricedLabel(buy, node.props, false)}</button>
      </div>
    </div>
  );
};
